/**
 * Time Slot Utility Functions
 * Reusable utilities for handling time slots in booking systems
 */

/**
 * Convert 24-hour time to 12-hour AM/PM format
 * @param hour - Hour in 24-hour format (0-23)
 * @param minute - Minute (0-59)
 * @returns Time string in 12-hour format (e.g., "9:00 AM", "2:30 PM")
 */
const formatTo12Hour = (hour: number, minute: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

/**
 * Parse 12-hour AM/PM time to 24-hour format
 * @param timeStr - Time string in 12-hour format (e.g., "9:00 AM", "2:30 PM")
 * @returns Object with hour and minute in 24-hour format
 */
const parse12HourTime = (timeStr: string): { hour: number; minute: number } => {
  const [time, period] = timeStr.split(' ');
  const [hourStr, minuteStr] = time.split(':');
  let hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);
  
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  
  return { hour, minute };
};

/**
 * Generate time slots from a time range string (API format)
 * @param range - Time range in format "09:00-17:00"
 * @param interval - Interval in minutes (default: 30)
 * @returns Array of time slots in 12-hour AM/PM format
 * 
 * @example
 * generateTimeSlotsFromRange("09:00-17:00")
 * // Returns: ["9:00 AM", "9:30 AM", "10:00 AM", ..., "5:00 PM"]
 */
export const generateTimeSlotsFromRange = (range: string, interval: number = 30): string[] => {
  const [start, end] = range.split('-');
  const slots: string[] = [];
  
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  
  const current = new Date();
  current.setHours(startHour, startMinute || 0, 0, 0);
  
  const endTime = new Date();
  endTime.setHours(endHour, endMinute || 0, 0, 0);
  
  while (current <= endTime) {
    const hours = current.getHours();
    const minutes = current.getMinutes();
    slots.push(formatTo12Hour(hours, minutes));
    
    current.setMinutes(current.getMinutes() + interval);
  }
  
  return slots;
};

/**
 * Parse franchise working hours format to 24-hour time range
 * @param hoursString - Format: "9:00 AM - 8:00 PM" or "Closed"
 * @returns Object with start and end times in 24-hour format, or null if closed
 * 
 * @example
 * parseWorkingHours("9:00 AM - 8:00 PM")
 * // Returns: { start: "09:00", end: "20:00" }
 * 
 * parseWorkingHours("Closed")
 * // Returns: null
 */
export const parseWorkingHours = (hoursString: string): { start: string; end: string } | null => {
  if (!hoursString || hoursString.toLowerCase() === 'closed') {
    return null;
  }

  const [startStr, endStr] = hoursString.split(' - ').map(s => s.trim());
  
  const convertTo24Hour = (timeStr: string): string => {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  return {
    start: convertTo24Hour(startStr),
    end: convertTo24Hour(endStr),
  };
};

/**
 * Generate time slots from franchise working hours
 * @param workingHours - Franchise working hours object
 * @param selectedDate - Date for which to generate slots
 * @param interval - Interval in minutes (default: 30)
 * @returns Array of time slots in 12-hour AM/PM format, or null if closed
 * 
 * @example
 * const hours = {
 *   "monday": "9:00 AM - 8:00 PM",
 *   "sunday": "Closed"
 * };
 * generateTimeSlotsFromWorkingHours(hours, new Date('2024-01-15'), 30)
 * // Returns: ["9:00 AM", "9:30 AM", "10:00 AM", ..., "8:00 PM"]
 */
export const generateTimeSlotsFromWorkingHours = (
  workingHours: Record<string, string>,
  selectedDate: Date,
  interval: number = 30
): string[] | null => {
  const dayName = selectedDate
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();
  
  const dayHours = workingHours[dayName];
  if (!dayHours) {
    return null;
  }

  const parsedHours = parseWorkingHours(dayHours);
  if (!parsedHours) {
    return null; // Closed
  }

  const slots: string[] = [];
  const [startHour, startMinute] = parsedHours.start.split(':').map(Number);
  const [endHour, endMinute] = parsedHours.end.split(':').map(Number);
  
  const current = new Date(selectedDate);
  current.setHours(startHour, startMinute, 0, 0);
  
  const end = new Date(selectedDate);
  end.setHours(endHour, endMinute, 0, 0);
  
  while (current <= end) {
    const hours = current.getHours();
    const minutes = current.getMinutes();
    slots.push(formatTo12Hour(hours, minutes));
    
    current.setMinutes(current.getMinutes() + interval);
  }
  
  return slots;
};

/**
 * Filter out past time slots based on current EST time (12-hour AM/PM format)
 * @param slots - Array of time slots in 12-hour AM/PM format ["9:00 AM", "10:00 AM", ...]
 * @param selectedDate - The date selected by user
 * @returns Filtered array with only future time slots
 * 
 * @example
 * // Current time: 10:30 AM, Selected: Today
 * filterPastTimeSlotsEST(["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM"], new Date())
 * // Returns: ["11:00 AM", "12:00 PM"]
 */
export const filterPastTimeSlotsEST = (slots: string[], selectedDate: Date): string[] => {
  const now = new Date(); // EST time
  const isToday = selectedDate.toDateString() === now.toDateString();
  
  if (!isToday) return slots;
  
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  return slots.filter(slot => {
    const { hour: slotHour, minute: slotMinute } = parse12HourTime(slot);
    
    // Compare hour and minute properly
    if (slotHour > currentHour) return true;
    if (slotHour === currentHour && slotMinute > currentMinute) return true;
    
    return false;
  });
};

/**
 * Sort time slots in chronological order (12-hour AM/PM format)
 * @param slots - Array of time slots in 12-hour AM/PM format
 * @returns Sorted array
 * 
 * @example
 * sortTimeSlotsEST(["2:00 PM", "9:00 AM", "11:00 AM"])
 * // Returns: ["9:00 AM", "11:00 AM", "2:00 PM"]
 */
export const sortTimeSlotsEST = (slots: string[]): string[] => {
  return slots.sort((a, b) => {
    const { hour: aHour, minute: aMinute } = parse12HourTime(a);
    const { hour: bHour, minute: bMinute } = parse12HourTime(b);
    
    // Compare hours first
    if (aHour !== bHour) {
      return aHour - bHour;
    }
    // If hours are equal, compare minutes
    return aMinute - bMinute;
  });
};
